/**
 * Models Service
 * 
 * Handles all operations related to the model registry including CRUD,
 * enabling/disabling models, and configuration.
 */

import logger from '../core/logger.js';
import { authService } from './auth-service.js';
import {
  ModelConfig,
  ModelsFilters,
  ListModelsResponse
} from '../types/prompt-model.js';

/**
 * Base API URL (configured via environment or defaults to relative path)
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Models Service Class
 */
class ModelsService {
  /**
   * List models with optional filters
   */
  async listModels(filters: ModelsFilters = {}): Promise<ListModelsResponse> {
    logger.info('models-service: Listing models', { filters });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'list',
          ...filters
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to list models' }));
        throw new Error(error.error || 'Failed to list models');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      logger.error('models-service: Failed to list models', { error, filters });
      throw error;
    }
  }
  
  /**
   * Get a single model by ID
   */
  async getModel(id: string): Promise<ModelConfig> {
    logger.info('models-service: Getting model', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'get',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to get model' }));
        throw new Error(error.error || 'Failed to get model');
      }
      
      const data = await response.json();
      return data.model;
      
    } catch (error) {
      logger.error('models-service: Failed to get model', { error, id });
      throw error;
    }
  }
  
  /**
   * Create a new model configuration (Admin only)
   */
  async createModel(
    modelData: Omit<ModelConfig, 'created_at' | 'updated_at'>
  ): Promise<ModelConfig> {
    logger.info('models-service: Creating model', { modelData });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          ...modelData
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to create model' }));
        throw new Error(error.error || 'Failed to create model');
      }
      
      const data = await response.json();
      return data.model;
      
    } catch (error) {
      logger.error('models-service: Failed to create model', { error, modelData });
      throw error;
    }
  }
  
  /**
   * Update an existing model configuration (Admin only)
   */
  async updateModel(
    id: string,
    updates: Partial<Omit<ModelConfig, 'id' | 'provider' | 'created_at' | 'updated_at'>>
  ): Promise<ModelConfig> {
    logger.info('models-service: Updating model', { id, updates });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'update',
          id,
          updates
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to update model' }));
        throw new Error(error.error || 'Failed to update model');
      }
      
      const data = await response.json();
      return data.model;
      
    } catch (error) {
      logger.error('models-service: Failed to update model', { error, id, updates });
      throw error;
    }
  }
  
  /**
   * Enable a model (Admin only)
   */
  async enableModel(id: string): Promise<ModelConfig> {
    logger.info('models-service: Enabling model', { id });
    
    return this.updateModel(id, { enabled: true });
  }
  
  /**
   * Disable a model (Admin only)
   */
  async disableModel(id: string): Promise<ModelConfig> {
    logger.info('models-service: Disabling model', { id });
    
    return this.updateModel(id, { enabled: false });
  }
  
  /**
   * Delete a model (Admin only)
   */
  async deleteModel(id: string): Promise<void> {
    logger.info('models-service: Deleting model', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'delete',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to delete model' }));
        throw new Error(error.error || 'Failed to delete model');
      }
      
    } catch (error) {
      logger.error('models-service: Failed to delete model', { error, id });
      throw error;
    }
  }
  
  /**
   * Get enabled models only (for selection in UI)
   */
  async getEnabledModels(): Promise<ModelConfig[]> {
    logger.info('models-service: Getting enabled models');
    
    const response = await this.listModels({ enabled: true });
    return response.models;
  }
}

/**
 * Singleton instance
 */
export const modelsService = new ModelsService();
